import React, { Fragment, memo, ReactNode } from 'react'
import { Menu, MenuItem, styled } from '@mui/material'
import styles from './PopupComponent.module.scss'

interface MenuItemType {
  id: string
  title: string
  subtitle?: string
  icon?: ReactNode
  onClick: (item: any) => void
  disabled?: boolean
  titleColor?: string
  keepOpen?: boolean
}

interface MenuType {
  items: MenuItemType[]
}

interface ButtonType {
  title?: string
  icon?: ReactNode
  styles?: React.CSSProperties
}

interface ContentType {
  menu?: MenuType
  component?: ReactNode
  title?: string
}

interface ConfigType {
  button?: ButtonType | any
  content?: ContentType
  container?: {
    styles?: React.CSSProperties
  }
}

interface PopupComponentProps {
  config: ConfigType
  returnItem?: any
  toggle?: (value: boolean) => void
  transitionDuration?: 'auto' | number
}

const PopupComponent: React.FC<PopupComponentProps> = ({
  config,
  returnItem,
  toggle = () => {},
  transitionDuration = 'auto',
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const open = Boolean(anchorEl)

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
    toggle(true)
  }

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation()
    setAnchorEl(null)
    toggle(false)
  }

  const onItemClick = (e: React.MouseEvent, item: MenuItemType) => {
    e.stopPropagation()
    item.onClick(returnItem || item)
    if (item?.keepOpen) return
    handleClose(e)
  }

  const buttonContent = () => {
    if (config.button?.title) {
      return <h4 className={styles.buttonTitle}>{config.button.title}</h4>
    }

    if (config.button?.icon) {
      return <div className={styles.centerItem}>{config.button.icon}</div>
    }

    return <Fragment>{config.button}</Fragment>
  }

  const popupContent = () => {
    if (config.content?.menu) {
      return (
        <div className={styles.contentContainer}>
          {config.content.menu.items.map((item) => (
            <MenuItem disabled={item.disabled} onClick={(e) => onItemClick(e, item)} key={item.id}>
              <div>
                <div className={styles.itemContainer}>
                  {item.icon && <div className={styles.iconContainer}>{item.icon}</div>}
                  <h4 className={styles.itemTitle} style={{ color: item.titleColor }}>
                    {item.title}
                  </h4>
                </div>
                {item.subtitle && (
                  <p className={styles.itemSubtitle} style={{ color: item.titleColor }}>
                    {item.subtitle}
                  </p>
                )}
              </div>
            </MenuItem>
          ))}
        </div>
      )
    }

    return <div>{config.content?.component}</div>
  }

  const StyledMenu = styled(Menu)`
    & .MuiPaper-root {
      border-radius: 8px;
    }

    & .MuiList-root {
      margin: 0;
      padding: 0;
    }
  `

  return (
    <div className={styles.container} style={config?.container?.styles || undefined}>
      <button
        onClick={handleClick}
        className={`${styles.button} ${config.button?.title && styles.buttonWithTitle}`}
        style={config?.button?.styles || undefined}
      >
        {buttonContent()}
      </button>
      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock={true}
        transitionDuration={transitionDuration}
      >
        {config.content?.title && (
          <div className={styles.contentTitleContainer}>
            <h2 className={styles.contentTitle}>{config.content.title}</h2>
          </div>
        )}
        {popupContent()}
      </StyledMenu>
    </div>
  )
}

export default memo(PopupComponent)
